import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';
import Character from '../../../models/Game Layer/character';
import Session from '../../../models/Game Layer/session';
import { AuthService } from '../../../services/auth-service';
import { GamelayerService } from '../../../services/gamelayer-service';
import { BackButtonComponent } from '../../back-button-component/back-button-component';
import { LoadingComponent } from '../../loading-component/loading-component';

type CharacterSessionCard = {
  session: Session;
  characterId: string;
};

@Component({
  selector: 'app-characters-component',
  standalone: true,
  imports: [CommonModule, RouterLink, BackButtonComponent, LoadingComponent],
  templateUrl: './characters-component.html',
  styleUrl: './characters-component.css',
})
export class CharactersComponent {
  private readonly auth = inject(AuthService);
  private readonly gamelayerService = inject(GamelayerService);

  readonly sessions = signal<Session[]>([]);
  readonly characterNames = signal<Record<string, string>>({});
  readonly isLoadingSessions = signal(true);
  readonly loadSessionsError = signal('');

  readonly characterSessions = computed<CharacterSessionCard[]>(() => {
    const currentUser = this.auth.currentUser();
    if (!currentUser) {
      return [];
    }

    return this.sessions()
      .map((session) => {
        const matchingPlayer = session.players?.find(
          (player) => player.userId === currentUser.id && !!player.characterId,
        );

        if (!matchingPlayer?.characterId) {
          return null;
        }

        return {
          session,
          characterId: matchingPlayer.characterId,
        };
      })
      .filter((entry): entry is CharacterSessionCard => entry !== null);
  });

  ngOnInit(): void {
    this.loadSessions();
  }

  private loadSessions(): void {
    this.isLoadingSessions.set(true);
    this.loadSessionsError.set('');

    this.gamelayerService.getSessions().subscribe({
      next: (response) => {
        const rawSessions = Array.isArray(response)
          ? response
          : (response as { sessions?: unknown[] })?.sessions ?? [];

        const sessions = rawSessions.map((session) => new Session(session));

        this.sessions.set(sessions);
        this.isLoadingSessions.set(false);
        this.loadCharacterNames(sessions);
      },
      error: (error) => {
        console.error('Error loading sessions:', error);
        this.loadSessionsError.set('The campaign ledger could not be opened right now.');
        this.isLoadingSessions.set(false);
      },
    });
  }

  private loadCharacterNames(sessions: Session[]): void {
    const joinedSessions = this.characterSessions();
    if (!joinedSessions.length) {
      this.characterNames.set({});
      return;
    }

    const sessionsById = new Map(sessions.map((session) => [session.id, session]));
    const requests = joinedSessions.map((entry) =>
      this.gamelayerService.getCharacters(entry.session.id),
    );

    forkJoin(requests).subscribe({
      next: (charactersBySession) => {
        const names = joinedSessions.reduce<Record<string, string>>((acc, entry, index) => {
          const session = sessionsById.get(entry.session.id);
          const characters = (charactersBySession[index] ?? []).map(
            (character) => new Character(character),
          );
          const character = characters.find((item) => item.id === entry.characterId);

          acc[this.getCharacterKey(entry.session.id, entry.characterId)] =
            character?.name || session?.name || 'Unknown Character';

          return acc;
        }, {});

        this.characterNames.set(names);
      },
      error: (error) => {
        console.error('Error loading character names:', error);
      },
    });
  }

  getCharacterName(sessionId: string, characterId: string): string {
    return this.characterNames()[this.getCharacterKey(sessionId, characterId)] || 'Unknown Character';
  }

  private getCharacterKey(sessionId: string, characterId: string): string {
    return `${sessionId}:${characterId}`;
  }
}
